/* eslint-disable no-plusplus */
/* eslint-disable no-console */

import React, {
  useMemo, useState, useRef, useEffect
} from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components/macro'
import {
  Icon, Button, Clamp, Title, Spacer
} from 'components/atoms'
import { InputIcon, PopoverActions, Modal } from 'components/molecules'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useToggle } from 'hooks'
import get from 'lodash.get'
import Fuse from 'fuse.js'
import { useEditorContext, actions } from 'context/EditorContext';
import FieldArrayDndHeader, { HeaderColumn } from './FieldArrayDndHeader'


// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const deleteItem = (list, index) => {
  const result = Array.from(list)
  // remove element from list
  result.splice(index, 1);
  return result
}

const updateItem = (list, values, index) => {
  const result = Array.from(list)
  result[index] = values
  return result
}

const addItem = (list, values, isReverse = false) => {
  const result = Array.from(list)
  if (isReverse) result.unshift(values)
  else result.push(values)
  return result
}

const addItemFiles = (list, values, isReverse = false) => {
  
  let fileitems;
  const result = Array.from(list)
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < values.length; i++) {
    fileitems = values[i];
    
    fileitems.name = fileitems.title
    delete fileitems.title
    delete fileitems.editable
    if (isReverse) result.unshift(fileitems)
    else result.push(fileitems)
  }
  return result
}


const getKey = (rowKey, item) => {
  if (typeof rowKey === 'function') return rowKey(item)
  if (typeof rowKey === 'string' && rowKey in item) return item[rowKey]
  console.error(`FieldArrayDnd 'rowKey' prop must be a function or a valid item key, rowKey: ${rowKey}`)
  return null
}

const FieldArrayDnd = ({
  initialProps,
  columns,
  ItemForm,
  itemFormProps,
  rowKey,
  onChange,
  newItemText,
  shouldHideHeaders,
  maxItems,
  minItems,
  shouldAddOnTop,
  shouldAddSearch,
  searchKeys,
  searchPlaceholder,
  shouldAllowReorder,
  disableClickOutside,
  extra,
  filemodule,
  isPeopleModule,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [items, setItems] = useState(initialProps)
  const [itemBeingEdited, setItemBeingEdited] = useState({ item: {}, index: -1 })

  const [isOpen, toggle] = useToggle(false)
  const form = useRef(null)

  const [{ peopleModuleselected }, dispatch] = useEditorContext();
  let peopleID = peopleModuleselected;

  const addItemPeople = (list, values, isReverse = false) => {
  
    let peopleitems;
    const result = Array.from(list)
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < values.length; i++) {
      peopleitems = values[i];
      peopleitems.contactsList = values[i].phoneExtension
      if (peopleitems.contactsList === undefined) {
        peopleitems.contactsList = '';
      }
      if (peopleitems.externalPhone !== undefined) {
        peopleitems.contactsList = `${peopleitems.contactsList} | ${values[i].externalPhone}`;
      }
      let auxEmail = values[i].email
      peopleitems.email = []
      peopleitems.email.push(auxEmail)
      delete peopleitems.phoneExtension
      peopleitems.id = values[i].photoId
      if (isReverse) result.unshift(values[i])
      else result.push(values[i])
    }
    return result
  }

  const columnProps = useMemo(() => {
    if (columns.length > 0) {
      return columns.map(({
        Header = null,
        accessor,
        Cell = null
      }) => {
        if (typeof accessor !== 'string') console.warn('Missing `accessor` prop at columns FieldArrayDnd')

        // eslint-disable-next-line react/prop-types, max-len
        const CellComponent = Cell === null ? ({ value }) => (<CellDefault>{value}</CellDefault>) : (props) => <Cell {...props} />

        const CustomHeader = typeof Header === 'string'
          ? (props) => (<HeaderColumn {...props}>{Header}</HeaderColumn>)
          : (props) => <Header {...props} />

        const HeaderComponent = Header === null
          ? (props) => (<HeaderColumn {...props}>{accessor}</HeaderColumn>)
          : CustomHeader

        return ({
          Cell: CellComponent,
          Header: HeaderComponent,
          accessor,
        })
      })
    }
    console.warn('Missing prop `columns` at FieldArrayDnd')
    return []
  }, [columns])

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(newItems);
    onChange(newItems)
  }

  const itemsWithKey = useMemo(() => items.map(item => (
    {
      ...item,
      draggableId: getKey(rowKey, item)
    }
  )), [items])

  const onDelete = (draggableId) => {
    const index = itemsWithKey.findIndex(item => item.draggableId === draggableId)
    const newItems = deleteItem(items, index)
    if (isPeopleModule) {
      let savePeople = []
      for (let i = 0; i < newItems.length; i++) {
        const obj = {
          id: newItems[i].photoId !== undefined ? newItems[i].photoId : newItems[i].id
        }
        savePeople.push(obj)
      }
      dispatch({
        type: actions.PEOPLE_MODULE_SELECT,
        data: savePeople,
      })
    }
    setItems(newItems);
    onChange(newItems)
  }

  const resetItemBeingEdited = () => {
    setItemBeingEdited({ item: {}, index: -1 })
  }

  const onEdit = (item, draggableId) => {
    const index = itemsWithKey.findIndex(item => item.draggableId === draggableId)
    setItemBeingEdited({ item, index })
    toggle()
  }

  const handleSubmitItem = (values, filemodule = true) => {
    let newItems = []
    
    if (!filemodule) {
      newItems = addItemFiles(items, values, shouldAddOnTop)
    }
    else {
      // when current item index is -1 it should add
      // eslint-disable-next-line no-lonely-if
      if (itemBeingEdited.index === -1) newItems = addItem(items, values, shouldAddOnTop)
      else newItems = updateItem(items, values, itemBeingEdited.index)
    }

    if (isPeopleModule) {
      newItems = addItemPeople(items, values, shouldAddOnTop)
      let savePeople = []
      for (let i = 0; i < newItems.length; i++) {
        const obj = {
          id: newItems[i].photoId !== undefined ? newItems[i].photoId : newItems[i].id
        }
        savePeople.push(obj)
      }
      dispatch({
        type: actions.PEOPLE_MODULE_SELECT,
        data: savePeople,
      })

      setItems(newItems)
      onChange(newItems)
      resetItemBeingEdited()
      toggle()
    }
    else {
      // when current item index is -1 it should add
      // eslint-disable-next-line no-lonely-if
      if (itemBeingEdited.index === -1) newItems = addItem(items, values, shouldAddOnTop)
      else newItems = updateItem(items, values, itemBeingEdited.index)
      setItems(newItems)
      onChange(newItems)
      resetItemBeingEdited()
      toggle()
    }
  }

  const handleFormSubmit = () => {
    if (form && form.current && form.current.submitForm) {
      form.current.submitForm()
    }
  }

  const handleAddItem = () => {
    resetItemBeingEdited()
    toggle()
  }

  const allowRemoveItem = useMemo(() => {
    if (typeof minItems === 'number' && items.length === minItems) return false
    return true
  }, [minItems, items]);

  const allowAddingItems = useMemo(() => {
    if (typeof maxItems === 'number' && items.length === maxItems) return false
    return true
  }, [maxItems, items])

  useEffect(() => {
    setItems(initialProps)
  }, [initialProps])

  const [search, setSearch] = React.useState('')
  const fuse = useMemo(() => new Fuse(itemsWithKey, {
    threshold: 0.1,
    keys: [rowKey, ...searchKeys]
  }), [items, searchKeys])

  const filteredItems = useMemo(() => {
    if (search === '') return itemsWithKey
    return fuse.search(search).map(({ item }) => item)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fuse])

  const shouldAllowReorderComputed = useMemo(
    () => shouldAllowReorder && filteredItems.length === items.length,
    [filteredItems, items, shouldAllowReorder]
  )
  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        disableClickOutside={disableClickOutside}
      >
        <Modal.Top>
          <Title>
            {itemBeingEdited.index === -1 ? 'Adicionar' : 'Editar'}
          </Title>
        </Modal.Top>
        <ItemForm
          ref={form}
          onSubmit={handleSubmitItem}
          initialValues={{ ...extra, ...itemBeingEdited.item }}
          {...itemFormProps}
        />
        <Modal.Actions>
          <Button
            type="button"
            modifiers={['marginRight', 'capitalize']}
            onClick={toggle}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            modifiers={['marginRight', 'capitalize', 'primary']}
            onClick={handleFormSubmit}
          >
            {itemBeingEdited.index === -1 ? 'Adicionar' : 'Editar'}
          </Button>
        </Modal.Actions>
      </Modal>
      <Wrapper>
        {shouldAddSearch && (
          <InputIcon
            icon="search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
        {!shouldHideHeaders && <FieldArrayDndHeader columnProps={columnProps} />}
        {(ItemForm && allowAddingItems && shouldAddOnTop) && (
          <>
            <Button
              modifiers={['stretch', 'leftAlign', 'bgLighterS', 'smallText']}
              icon="fal fa-plus"
              onClick={handleAddItem}

            >
              {!filemodule ? 'Adicionar Ficheiros' : newItemText}
            </Button>
            <Spacer bottom="xs" />
          </>
        )}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="slider">
            {provided => (
              <Container ref={provided.innerRef} {...provided.droppableProps}>
                {filteredItems.map((item, index) => (
                  <Draggable key={item.draggableId} draggableId={item.draggableId} index={index}>
                    {(providedDraggable, snapshot) => (
                      <Row
                        ref={providedDraggable.innerRef}
                        {...providedDraggable.draggableProps}
                        isDragging={snapshot.isDragging}
                      >
                        <Column
                          small
                          hide={!shouldAllowReorderComputed}
                          {...providedDraggable.dragHandleProps}
                        >
                          <Icon icon="drag-handle" height={24} />
                        </Column>
                        {columnProps.map(({
                          Cell,
                          accessor,
                        }) => (
                          [filemodule ? [
                            <Column
                              key={accessor}
                              onClick={() => onEdit(item, item.draggableId)}
                              hasInitialSpacing={!shouldAllowReorderComputed}
                            >
                              <Cell value={get(item, accessor)} original={item} />
                            </Column>
                          ] : [
                            <Column
                              key={accessor}
                              onClick={!isPeopleModule ? () => onEdit(item, item.draggableId) : null}
                              hasInitialSpacing={!shouldAllowReorderComputed}
                            >
                              <Cell value={get(item, accessor)} original={item} />
                            </Column>
                          ]]
                        ))
                        }
                        <Column small>
                          <PopoverActions
                            actions={[
                              ...(ItemForm && isPeopleModule ? [{
                                text: 'Editar',
                                onClick: () => onEdit(item, item.draggableId),
                                icon: 'edit',
                              }] : []),
                              ...(ItemForm && filemodule ? [{
                                text: 'Apagar',
                                onClick: () => onDelete(item.draggableId),
                                icon: 'delete',
                                disabled: !allowRemoveItem
                              }] : [])
                            ]}
                            // eslint-disable-next-line no-unneeded-ternary
                            // disabled={filemodule ? false : true}
                            position="bottom"
                            handlerWidth={35}
                          />
                        </Column>
                      </Row>
                    )
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
              </Container>
            )}
          </Droppable>
        </DragDropContext>
        {(ItemForm && allowAddingItems && !shouldAddOnTop) && (
          <Button
            modifiers={['removeBottomBorder', 'stretch', newItemText ? 'spaceBetween' : 'alignLeft', 'bgLighterS', 'smallText']}
            icon="fal fa-plus"
            onClick={handleAddItem}
            newItemText={newItemText}
          >
            {!filemodule ? 'Adicionar Ficheiros' : newItemText}
          </Button>
        )}
        <Line />
      </Wrapper>
    </>
  )
}

FieldArrayDnd.propTypes = {
  /**
    * Array of objects with the initial props to create the initial rows & columns
    */
  initialProps: PropTypes.arrayOf(PropTypes.shape({})),
  /**
    * Defines what columns to show.
    * Header: defines the column header, can be a custom component or string
    * accessor: the key of the property refering to the column
    * Cell: the column cell of this property, can be a custom component or string
    */
  columns: PropTypes.arrayOf(PropTypes.shape({
    Header: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
    accessor: PropTypes.string.isRequired,
    Cell: PropTypes.node,
  })),
  /**
    * Object form (formik), required if you want to add and edit items
    */
  ItemForm: PropTypes.node,
  /**
    * Extra props to be spread into ItemForm
    */
  itemFormProps: PropTypes.shape({}),
  /**
   * To define what will be the key of each row, required for the drag & drop feature,
   * Can be a function that returns a string or a string
   */
  rowKey: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
  onChange: PropTypes.func,
  /**
   * Custom new item button's text
   */
  newItemText: PropTypes.bool,
  /**
   * Hides column headers
   */
  shouldHideHeaders: PropTypes.bool,
  /**
   * Sets limit number of items
   */
  maxItems: PropTypes.number,
  /**
   * shouldAddOnTop
   */
  shouldAddOnTop: PropTypes.bool,
  /**
   * Adds a search bar, use with the prop searchKeys[]
   */
  shouldAddSearch: PropTypes.bool,
  /**
   * keys used for search filters
   */
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * keys used for search filters
  */
  searchPlaceholder: PropTypes.string,
  /**
   * Can be used to disable drag & drop re-ordering
   */
  shouldAllowReorder: PropTypes.bool,
  extra: PropTypes.shape({}),
  minItems: PropTypes.number,
  /**
   * use this property to disable click outside so that you can
   * have a modal opening inside a Modal and still allow you to
   * click on "edit" and "delete" buttons
   */
  disableClickOutside: PropTypes.bool
}

FieldArrayDnd.defaultProps = {
  initialProps: [],
  columns: [],
  ItemForm: null,
  itemFormProps: {},
  onChange: () => { },
  newItemText: 'Novo item',
  shouldHideHeaders: false,
  maxItems: null,
  shouldAddOnTop: false,
  shouldAddSearch: false,
  shouldAllowReorder: true,
  searchPlaceholder: 'pesquisar lista',
  searchKeys: [],
  extra: {},
  minItems: 1,
  disableClickOutside: false,
  filemodule: true
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const CellDefault = styled(Clamp)``

const Column = styled.div`
  cursor: ${props => (props.draggable ? 'grab' : 'pointer')};
  font-size: ${props => props.theme.sizes.s};
  font-weight: ${props => props.theme.weight.light};
  align-items: ${props => (props.center ? 'center' : 'flex-start')};
  padding-top: ${props => props.theme.spacing.m};
  padding-bottom: ${props => props.theme.spacing.m};
  justify-content: center;
  flex-grow: ${props => (props.small ? 0.2 : 1)};
  max-width: ${props => (props.small ? '80px' : 'none')};
  overflow-x: scroll;
  flex-shrink: 0;
  flex-basis: 24px;
  margin-right: ${props => props.theme.spacing.xs};
  /* width */
  ::-webkit-scrollbar {
    height: ${props => props.theme.sizes.xs3};
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.borderLighter};
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background-color: ${props => props.theme.colors.bgDarker};
  }

  ${({ hide }) => hide && css`
    pointer-events: none;
    visibility: hidden;
    width: 0px;
    position: absolute;
  `}

  ${({ hasInitialSpacing }) => hasInitialSpacing && css`
    padding-left: ${props => props.theme.spacing.m};
  `}
`;

const Row = styled.div`
  border-bottom: 2px solid ${props => props.theme.colors.borderLighter};
  display: flex;
  margin-bottom: 8px;
  background-color: ${props => (props.isDragging ? props.theme.colors.bg : 'transparent')};
  border-radius: 4px;
  align-items: center;
  ${Column} {
    overflow-x: ${props => (props.isDragging ? 'hidden' : 'scroll')} !important;
    overflow-y: hidden;
}
  &:hover {
    background-color: ${props => props.theme.colors.bg};
  }
`;

const Container = styled.div`
  ${Row}:last-child {
    border-bottom: 0px;
    margin-bottom: 8px;
  }
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  border-bottom: 1px solid ${props => props.theme.colors.base};
`;

export default FieldArrayDnd
